/**
 * CDP SDK Core
 * Main entry point with plugin system
 */

import type {
  CDPCoreConfig,
  CDPCoreAPI,
  CDPState,
  CDPPlugin,
  ConsentPreferences,
  ConsentType,
  EventContext,
  SessionData,
  DeviceFingerprint,
  EventPayload,
  BeforeSendCallback,
  AfterSendCallback,
  InitializedCallback,
} from './types';
import { DEFAULT_CONFIG, SDK_NAME, SDK_VERSION } from './types';
import type { SiteProfile } from './types';
import { setDebugMode, log, warn, deepMerge, isBrowser } from './utils';
import {
  getOrCreateAnonymousId,
  getAnonymousId,
  getUserId,
  clearUserId,
  setUserId,
  resetIdentity,
  getOrCreateEphemeralId,
} from './identity';
import { getOrCreateSession, getSession, touchSession, incrementPageViews } from './session';
import { buildEventContext, resetContextEpoch } from './context';
import {
  initConsent,
  updateConsent,
  getConsentStatus,
  hasConsent as checkConsent,
  setConsentState,
  hasStoredConsent,
} from './consent';
import { loadSiteConfig, peekProfile } from './site-config';
import { resolveInitialConsent, initTcfBridge, detectGpcDnt, waitsForTcf, TCF_WAIT_MS } from './consent-mode';
import { showConsentBanner as renderConsentBanner, hideConsentBanner, detectCmp, initCmpBridges } from './consent-banner';
import { readConsentBridge, watchConsentBridge } from './consent-bridge';
import {
  mergePersistedQueue,
  persistQueue,
  hasPendingEvents,
} from './queue';
import { sendBeacon, sendConsentToServer } from './transport';
import {
  getStorageString,
  setStorageString,
  setStorage,
  removeStorage,
  deleteHybrid,
  setPersistenceAllowed,
  isPersistenceAllowed,
} from './storage';
import { STORAGE_KEYS } from './types';

// =============================================================================
// CONSENT SYNC — state, not page loads
// =============================================================================

const CONSENT_SYNC_KEY = 'cdp_consent_synced';

/**
 * Push a consent state to the server ONLY when it differs from the last state
 * this browser synced. Until 2026-08-20 every init() re-POSTed the same
 * preferences: 112k POST + 51k OPTIONS per hour on the CDP edge (43% of all
 * its requests) for a value that almost never changes. The implied posture is
 * never synced at all — the server derives it from the Site Profile and keeps
 * no per-visitor ledger for it; only an explicit decision (site/CMP/TCF) is a
 * record worth keeping.
 */
function syncConsent(preferences: ConsentPreferences, mode: 'implied' | 'explicit'): void {
  if (!state.config || mode === 'implied') return;
  // Under an implied site posture "everything granted" — whether a TCF CMP, a
  // site call or a stored cookie says so — carries nothing the posture doesn't
  // (the server ignores it too). Measured 2026-08-21: 28k such POSTs/hour from
  // dir.bg/24chasa's TCF bridge, one per browser. Any withheld purpose IS a
  // decision and is synced.
  const siteMode = state.siteProfile.consent?.mode || 'implied';
  if (siteMode === 'implied' && preferences.analytics && preferences.marketing
      && preferences.personalization && preferences.functional) return;
  const granted = !!(preferences.analytics || preferences.marketing || preferences.personalization);
  const sig = [
    state.anonymousId || '',
    state.userId || '',
    mode,
    JSON.stringify(Object.keys(preferences).sort().map((k) => [k, !!preferences[k as keyof ConsentPreferences]])),
  ].join('|');
  if (getStorageString(CONSENT_SYNC_KEY) === sig) return;
  // Nothing granted and nothing previously synced = nothing to record.
  if (!granted && !getStorageString(CONSENT_SYNC_KEY)) return;
  setStorageString(CONSENT_SYNC_KEY, sig);
  sendConsentToServer(
    state.config.apiHost,
    state.config.writeKey,
    state.anonymousId,
    state.userId,
    preferences,
    mode
  ).then((ok) => {
    // A failed sync must retry on the next load, not be remembered as done.
    if (!ok) setStorageString(CONSENT_SYNC_KEY, '');
  });
}
import { flushBatchOnUnload } from './batch';
import { startAuthIdentity, identityUid, normalizeEmail } from './auth-identity';
import {
  createPageEvent,
  createTrackEvent,
  createIdentifyEvent,
  createGroupEvent,
  processAndSendEvent,
  flushQueue,
  setExcludedPathsSource,
} from './events';

// =============================================================================
// STATE
// =============================================================================

const state: CDPState = {
  initialized: false,
  config: null,
  anonymousId: null,
  userId: null,
  consent: {},
  session: null,
  deviceFingerprint: null,
  plugins: new Map(),
  beforeSendCallbacks: [],
  afterSendCallbacks: [],
  initializedCallbacks: [],
  siteProfile: {},
  liteMode: false,
};

// No device fingerprint, in any mode, since 20.10 (monfri decision ЛО-13, LA-05 in
// docs/legal/audits/LEGAL-FULL-AUDIT-2026-10-20.md): canvas, WebGL and audio are never
// read and `context.device` is never sent; `privacy.fingerprint` in the Site Profile
// and the `enableFingerprint` option are ignored. Nothing measurable is lost — the two
// server bot checks that read it (`webgl_software`, `ua_mismatch`) caught 0 bots in
// 7 days on dir.bg, 24chasa and Paga Shop (20.10), and since PROF-1 (01.07) a
// fingerprint match alone never merged two profiles. The screen-size and
// `navigator.webdriver` checks need none of it (context.ts getScreenContext /
// getAutomationSignals). `state.deviceFingerprint` stays null; the server still
// accepts the field from older bundles.

// Resolves when the freshest Site Profile is available (see site-config.ts).
let siteProfileReady: Promise<SiteProfile> = Promise.resolve(state.siteProfile);
// Disposer for the IAB TCF bridge, if installed.
let tcfDispose: (() => void) | null = null;

// Sends held until the TCF CMP answers (LA-06, see waitsForTcf in consent-mode.ts):
// null = nothing is held, an array = the sends waiting, in order.
let tcfWaiting: Array<() => void> | null = null;
let tcfWaitTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Run a send now, or — while an implied page waits for its TCF CMP — once the CMP
 * has answered. The event is BUILT at call time (its URL, title and timing are the
 * page's as it was); only consent, the beforeSend hooks and the send itself wait,
 * so a refusal in the answer applies to it.
 */
function afterTcfAnswer(send: () => void): void {
  if (tcfWaiting) tcfWaiting.push(send);
  else send();
}

/**
 * The wait is over: the CMP answered (its choice is already applied), a site called
 * cdp.consent(), or TCF_WAIT_MS passed — and then the mode decides as it would on a
 * page without TCF (implied: everything). Idempotent.
 *
 * An unload during the wait sends nothing that was held: the held sends are not in
 * the batch the unload flushes, and they are not persisted for the next page either,
 * which would send them there without the CMP's answer. The cost is the page view
 * of a visitor who leaves within the wait while the CMP has not answered.
 */
function endTcfWait(): void {
  const waiting = tcfWaiting;
  if (!waiting) return;
  tcfWaiting = null;
  if (tcfWaitTimer) {
    clearTimeout(tcfWaitTimer);
    tcfWaitTimer = null;
  }
  for (const send of waiting) {
    try {
      send();
    } catch (e) {
      warn('Held event failed:', e);
    }
  }
}

// Whether the visitor has made (or a CMP has supplied) an explicit consent
// choice — once true, the lite/default posture no longer applies on refresh.
let explicitConsentGiven = false;
// Guard so the internal lite-mode beforeSend is registered only once.
let liteBeforeSendRegistered = false;
// Disposer of the non-TCF consent-manager bridges (Cookiebot, CookieYes, …).
let cmpDispose: (() => void) | null = null;
// Disposer of the watcher of the site's own consent choice (consent.bridge).
let bridgeDispose: (() => void) | null = null;

function anythingGranted(c: ConsentPreferences): boolean {
  return !!(c.analytics || c.marketing || c.personalization);
}

/**
 * Whether the site asks its visitors for consent (any mode but `implied`, or the
 * Monfri banner on). Everything СТ22 added — the persistence gate, a stored
 * "no" honoured from the first event, identifiers removed on withdrawal — applies
 * only here. An implied site keeps exactly its old behaviour (measured 07.10:
 * every existing workspace is implied); changing what dir.bg and 24chasa count
 * is the operator's call, not a side effect of a new default.
 */
function asksForConsent(profile: SiteProfile = state.siteProfile): boolean {
  const c = profile?.consent;
  return (!!c?.mode && c.mode !== 'implied') || c?.banner === true;
}

/** The persistence gate for the current posture. */
function gateFor(): boolean {
  return !asksForConsent() || (!state.liteMode && anythingGranted(state.consent));
}

/**
 * The visitor withdrew everything: stop writing to the device and forget what
 * the SDK left there (СТ22). The decision cookie stays — it is the "no".
 */
function forgetIdentifiers(): void {
  setPersistenceAllowed(false);
  const domain = state.config?.cookieDomain;
  // The profile cache and the event queue too: a cold start runs on the built-in
  // implied default until the profile lands, and a direct (non-loader) install
  // may have written both in that window.
  for (const key of [STORAGE_KEYS.ANONYMOUS_ID, STORAGE_KEYS.USER_ID, STORAGE_KEYS.SESSION,
    STORAGE_KEYS.DEVICE_FINGERPRINT, STORAGE_KEYS.EMAIL_CLICK_ID, STORAGE_KEYS.SITE_CONFIG,
    STORAGE_KEYS.EVENT_QUEUE]) {
    try { deleteHybrid(key, domain); } catch { /* storage blocked */ }
  }
  state.anonymousId = getOrCreateEphemeralId();
  state.userId = null;
}

/** Fold the relevant Site Profile fields into the runtime config. */
function applyProfileToConfig(config: CDPCoreConfig, profile: SiteProfile): void {
  // The profile supplies the managed cookie domain unless the snippet set one.
  if (profile.cookieDomain && !config.cookieDomain) {
    config.cookieDomain = profile.cookieDomain;
  }
  // `privacy.fingerprint` is no longer read: no mode fingerprints (ЛО-13, see the
  // note at the state above). The option stays off whatever a snippet passes.
  // The old reasoning about what 'off' changed (the device_fingerprint identity
  // link and the bot denylist entries — never cookie stitching since PROF-1) is in
  // cdp docs/audits/PROFILE-OVERMERGE-24CHASA-2026-09-22.md §7.
  config.enableFingerprint = false;
}

/** Register a one-time beforeSend that de-identifies events while in lite mode. */
function ensureLiteBeforeSend(): void {
  if (liteBeforeSendRegistered) return;
  liteBeforeSendRegistered = true;
  state.beforeSendCallbacks.push((payload: EventPayload) => {
    if (!state.liteMode) return payload;
    const p = { ...payload, userId: null } as EventPayload & {
      properties?: Record<string, unknown>;
    };
    if (p.type === 'page' || p.type === 'track') {
      p.properties = { ...(p.properties || {}), anonymous: true };
    }
    return p;
  });
}

/**
 * Apply a consent signal (from the site's cdp.consent() call or an IAB TCF CMP).
 * Upgrades out of the lite posture when newly permitted, and syncs the grant to
 * the server. `persist` writes the choice to storage (true for an explicit
 * user/CMP decision).
 */
function applyConsentSignal(preferences: ConsentPreferences, persist: boolean): ConsentPreferences {
  if (!state.config) return state.consent;
  explicitConsentGiven = true;

  const merged = persist
    ? updateConsent(preferences, state.config.cookieDomain)
    : (setConsentState({ ...getConsentStatus(), ...preferences }), getConsentStatus());
  state.consent = merged;

  const granted = anythingGranted(merged);
  // A decision was made — whatever the page offered (our banner) is answered.
  hideConsentBanner();

  // Upgrade out of the anonymous lite posture — or out of the memory-only one a
  // strict/cmp-only site runs before a decision — once anything is granted: open
  // the persistence gate and mint a real persistent ID so the now-consented
  // visitor is recognised across visits.
  if ((state.liteMode || !isPersistenceAllowed()) && granted) {
    state.liteMode = false;
    setPersistenceAllowed(true);
    state.anonymousId = getOrCreateAnonymousId(
      state.config.cookieExpiration,
      state.config.cookieDomain
    );
    if (state.session) setStorage(STORAGE_KEYS.SESSION, state.session);
    log('Consent granted — upgraded to full tracking');
  } else if (!granted && persist && isPersistenceAllowed() && asksForConsent()) {
    // Everything withdrawn: nothing more on the device, and no identifier left behind.
    forgetIdentifiers();
    log('Consent withdrawn — identifiers removed');
  }

  // An explicit signal (site call / CMP / TCF) is a decision — sync grants AND
  // revocations, once, when they change.
  syncConsent(merged, 'explicit');

  // Plugins that wait for a consent (the push opt-in banner waits for marketing
  // on strict / cmp-only sites, 2026-09-28) hear about it here instead of polling.
  try {
    if (typeof window !== 'undefined' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('cdp:consent', { detail: { ...merged } }));
    }
  } catch { /* an old browser without CustomEvent simply keeps the banner away */ }
  return merged;
}

/**
 * СТ22 (launch 07.10). On a site that asks for consent (any mode but `implied`,
 * or `consent.banner`), bridge the site's own consent manager — Cookiebot,
 * CookieYes, Complianz, OneTrust (TCF is bridged on every site) — and, when the
 * profile turns the banner on and nothing has decided yet, offer the Monfri
 * banner. An implied site keeps exactly what it had.
 */
function offerConsent(): void {
  const cfg = state.siteProfile.consent;
  const asksForConsent = (!!cfg?.mode && cfg.mode !== 'implied') || cfg?.banner === true;
  if (!asksForConsent) return;
  if (!cmpDispose) cmpDispose = initCmpBridges((c) => applyConsentSignal(c, true));
  if (cfg?.banner !== true || explicitConsentGiven) return;
  if (cfg.honorGpcDnt !== false && detectGpcDnt()) return;
  // A consent manager may load after us: give it a moment before offering ours.
  setTimeout(() => {
    if (explicitConsentGiven || detectCmp()) return;
    renderConsentBanner({
      language: state.siteProfile.language,
      policyUrl: cfg.policyUrl,
      onDecision: (c) => consent(c),
    });
  }, 1200);
}

/** The site's own "Cookie settings" link: offer the banner again. */
function showConsentBannerFn(): void {
  if (!state.initialized) return;
  renderConsentBanner({
    language: state.siteProfile.language,
    policyUrl: state.siteProfile.consent?.policyUrl,
    onDecision: (c) => consent(c),
  });
}

/**
 * Re-evaluate the consent/identity posture when the Site Profile refresh lands
 * on a cold start (the first paint used built-in defaults). No-op once the
 * visitor or a CMP has given an explicit choice, or on a warm cache.
 */
function reapplyPostureFromProfile(): void {
  if (!state.config || explicitConsentGiven) return;
  applyProfileToConfig(state.config, state.siteProfile);
  const resolved = resolveInitialConsent(state.siteProfile, null);
  state.consent = resolved.consent;
  setConsentState(resolved.consent);
  const wasLite = state.liteMode;
  state.liteMode = resolved.lite;
  // The gate follows the real profile (СТ22). A cold start ran on the built-in
  // implied default; if the site asks for consent, take back what was written.
  const wasOpen = isPersistenceAllowed();
  setPersistenceAllowed(gateFor());
  if (wasOpen && !isPersistenceAllowed()) {
    forgetIdentifiers();
  }
  offerConsent();
  // Profile turned out to be non-lite (e.g. 'implied'): upgrade identity now.
  if (wasLite && !state.liteMode) {
    state.anonymousId = getOrCreateAnonymousId(
      state.config.cookieExpiration,
      state.config.cookieDomain
    );
    // Posture came from the Site Profile, not from the visitor: implied → the
    // server derives it, nothing to sync.
    syncConsent(state.consent, 'implied');
  }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * A plugin's onInit reads the page — the body's classes, the product's markup, the checkout fields. The tag is an
 * async script in <head> (the Monfri WordPress plugin prints it at wp_head priority 1), and with the bundles in the
 * browser's cache it runs before the parser reaches <body>: WooCommerce's onInit threw on `document.body` null and
 * the page lost Product Viewed, Product Added and the checkout capture (live test, monfri 20.10). Until the document
 * is parsed, the plugins and the first page view wait for DOMContentLoaded; consent, the unload handler and the
 * banner do not.
 */
function whenDocumentParsed(fn: () => void): void {
  if (typeof document === 'undefined' || document.readyState !== 'loading') {
    fn();
    return;
  }
  document.addEventListener('DOMContentLoaded', () => fn(), { once: true });
}

function runPluginInit(plugin: CDPPlugin, config: CDPCoreConfig): void {
  if (!plugin.onInit) return;
  try {
    plugin.onInit(config);
  } catch (e) {
    warn('Plugin onInit error:', plugin.name, e);
  }
}

/** A plugin registered after init: its onInit waits for the parsed document too (unless it was removed meanwhile). */
function initPlugin(plugin: CDPPlugin, config: CDPCoreConfig): void {
  whenDocumentParsed(() => {
    if (state.plugins.get(plugin.name) === plugin) runPluginInit(plugin, config);
  });
}

/**
 * Initialize the CDP SDK
 */
function init(writeKey: string, options: Partial<CDPCoreConfig> = {}): void {
  if (!isBrowser()) {
    warn('CDP SDK can only be initialized in a browser environment');
    return;
  }

  if (state.initialized) {
    warn('CDP SDK is already initialized');
    return;
  }

  // Merge config with defaults
  const config: CDPCoreConfig = deepMerge(
    { ...DEFAULT_CONFIG, writeKey, apiHost: '' } as CDPCoreConfig,
    options
  );

  if (!config.apiHost) {
    warn('apiHost is required');
    return;
  }

  // 0. The persistence gate starts closed. Open it before anything is written
  // unless the profile in hand asks for consent — a site that does not (implied,
  // every site before 07.10) keeps writing exactly what it always wrote; a cold
  // start with no profile yet behaves as before too.
  const early = peekProfile(config.writeKey);
  if (!early || !asksForConsent(early)) setPersistenceAllowed(true);

  // 1. Resolve the Site Profile (sync best-effort + background SWR refresh).
  const handle = loadSiteConfig(config);
  state.siteProfile = handle.current;
  siteProfileReady = handle.ready;
  applyProfileToConfig(config, state.siteProfile);
  // Р-ID8: the site's own pages that send nothing, read per event from the live profile.
  setExcludedPathsSource(() => state.siteProfile?.privacy?.excludePaths);

  state.config = config;

  // Set debug mode
  setDebugMode(config.debug || false);

  log('Initializing CDP SDK v' + SDK_VERSION);
  log('Config:', { ...config, writeKey: config.writeKey.slice(0, 8) + '...' });

  // 2. Resolve the consent posture (mode + any stored decision + GPC/DNT).
  const stored = initConsent();
  const storedHasChoice = anythingGranted(stored);
  // The site's own choice (consent.bridge — a shop's banner) wins over anything stored: it is read on every page and
  // applied for the page only (core/consent-bridge.ts).
  const bridged = readConsentBridge(state.siteProfile.consent?.bridge);
  // A stored "no" counts as a decision only where the site asks for consent; an
  // implied site reads stored preferences exactly as before.
  const decided = bridged ? true : asksForConsent() ? hasStoredConsent() : storedHasChoice;
  explicitConsentGiven = decided;
  const resolved = resolveInitialConsent(
    state.siteProfile,
    bridged ?? (decided ? stored : null)
  );
  state.liteMode = resolved.lite;
  state.consent = resolved.consent;
  // Lite/implied consent is in-memory only — it is NOT a stored "choice". A real
  // explicit grant is persisted later via consent().
  if (!decided || bridged) {
    setConsentState(resolved.consent);
  }
  // The persistence gate (СТ22): the device is written to under the implied
  // posture and once the visitor granted something — never in the anonymous lite
  // posture and never before a decision on a strict/cmp-only site.
  setPersistenceAllowed(gateFor());

  // 3. Identity — before consent the ID lives in memory only (the gate keeps it
  // off the device), so pre-consent traffic is counted, not followed.
  if (state.liteMode || !isPersistenceAllowed()) {
    state.anonymousId = getOrCreateEphemeralId();
    state.userId = null;
    log('CDP running without a stored identity until consent');
  } else {
    state.anonymousId = getOrCreateAnonymousId(config.cookieExpiration, config.cookieDomain);
    state.userId = getUserId();
    // Р-ID6: a userId stored by a bundle before 2.4.0 may be the shopper's e-mail — replace it with the
    // salted hash so the address leaves the cookie (the profile keeps the e-mail it already has).
    const storedEmail = normalizeEmail(state.userId);
    if (storedEmail) {
      void identityUid(config.writeKey, storedEmail)
        .then((uid) => {
          if (state.userId !== null && normalizeEmail(state.userId) === storedEmail) {
            setUserId(uid, config.cookieExpiration, config.cookieDomain);
            state.userId = uid;
          }
        })
        .catch(() => {});
    }
  }

  // 4. Session
  state.session = getOrCreateSession(config.sessionTimeout);

  // 5. Sync consent to the server only when it is an explicit stored choice AND
  // differs from what this browser last synced (see syncConsent). The implied
  // posture is never posted.
  // A stored ALL under the implied posture is not a decision (older bundles
  // persisted the implied default into the cookie) — only a stored choice that
  // withholds something is worth syncing.
  const storedIsDefaultAll = storedHasChoice && (state.siteProfile.consent?.mode || 'implied') === 'implied'
    && !!(stored.analytics && stored.marketing && stored.personalization && stored.functional);
  if (!state.liteMode) {
    syncConsent(state.consent, bridged || (storedHasChoice && !storedIsDefaultAll) ? 'explicit' : 'implied');
  }

  // 6. No fingerprint (ЛО-13). Bundles before 20.10 kept one in localStorage for 30
  // days; it is not read any more, so it goes.
  removeStorage(STORAGE_KEYS.DEVICE_FINGERPRINT);

  // LA-06: an implied page with a TCF CMP holds its events until the CMP answers
  // (or TCF_WAIT_MS). Started before the first page view below is built, and before
  // the bridge is installed, since a CMP that has already loaded answers at once.
  if (waitsForTcf(state.siteProfile)) {
    tcfWaiting = [];
    tcfWaitTimer = setTimeout(endTcfWait, TCF_WAIT_MS);
    log('Waiting for the TCF CMP before sending');
  }

  // 7. Stamp de-identified events while in lite mode.
  ensureLiteBeforeSend();

  // Merge any persisted queue
  mergePersistedQueue();

  // Mark as initialized
  state.initialized = true;

  // The plugins, the initialized callbacks and the first page view wait until the document is parsed
  // (whenDocumentParsed) — in this order, as before: a plugin's beforeSend (news: no page view on an article) must
  // know the page before the page view goes out.
  whenDocumentParsed(() => {
    for (const plugin of state.plugins.values()) {
      runPluginInit(plugin, config);
    }

    // Notify callbacks
    for (const callback of state.initializedCallbacks) {
      try {
        callback();
      } catch (e) {
        warn('Initialized callback error:', e);
      }
    }

    // Auto-track page view
    if (config.autoTrack) {
      page();

      // Setup SPA navigation tracking
      setupNavigationTracking();
    }

    // The site's signed-in readers (NEWS-LOGIN-IDENTITY-2026-10-26) — on the freshest profile, since the
    // `identity` block is ours and a cold start has only the defaults.
    void siteProfileReady.then(() => {
      try {
        startAuthIdentity({
          profile: () => state.siteProfile,
          writeKey: config.writeKey,
          cookieDomain: config.cookieDomain,
          getUserId: () => state.userId,
          hasPersonalization: () => hasConsent('personalization'),
          whenConsentKnown: afterTcfAnswer,
          sendIdentity,
          track: (event, properties) => track(event, properties),
          reset,
        });
      } catch (e) {
        warn('Auth identity failed to start:', e);
      }
    });

    // Flush any queued events — after the CMP's answer too (LA-06): the queue is
    // checked against the consent in force when it is flushed.
    if (hasPendingEvents()) {
      afterTcfAnswer(() => {
        void flushQueue(
          config,
          state.beforeSendCallbacks,
          state.afterSendCallbacks
        );
      });
    }
  });

  // Setup page unload handler
  setupUnloadHandler();

  // Bridge an IAB TCF v2.2 CMP if present (only ever applies grants it reports).
  // Its answer ends the wait for it (LA-06) once the choice is applied.
  if (state.siteProfile.consent?.tcf !== false) {
    tcfDispose = initTcfBridge(state.siteProfile, (c) => applyConsentSignal(c, true), endTcfWait);
  }

  // СТ22: the site's other consent managers, and the Monfri banner when it has none.
  offerConsent();

  // The site's own choice changing on this page (its banner) — applied without writing cdp_consent.
  if (bridgeDispose) bridgeDispose();
  bridgeDispose = watchConsentBridge(state.siteProfile.consent?.bridge, bridged, (c) => {
    applyConsentSignal(c, false);
    log('Consent: the site\'s own choice changed');
  });

  // On a cold start the first paint used built-in defaults; re-evaluate the
  // posture once the freshest profile lands (no-op on a warm cache).
  void siteProfileReady.then(() => {
    if (!explicitConsentGiven) reapplyPostureFromProfile();
  });

  log('CDP SDK initialized:', state.anonymousId);
}

/**
 * Check if SDK is initialized
 */
function isInitialized(): boolean {
  return state.initialized;
}

/**
 * Get current config
 */
function getConfig(): CDPCoreConfig | null {
  return state.config;
}

/**
 * Get the resolved Site Profile (defaults ← remote ← inline).
 */
function getSiteProfileFn(): SiteProfile {
  return state.siteProfile;
}

/**
 * Resolves with the freshest Site Profile (immediately on a warm cache or when
 * remoteConfig is off; bounded by the fetch timeout on a cold start).
 */
function whenConfigReadyFn(): Promise<SiteProfile> {
  return siteProfileReady;
}

// =============================================================================
// TRACKING METHODS
// =============================================================================

/**
 * Track a page view
 */
function page(
  name?: string,
  category?: string,
  properties?: Record<string, unknown>
): void {
  if (!state.initialized || !state.config) {
    warn('CDP SDK not initialized. Call init() first.');
    return;
  }

  // Increment page views
  incrementPageViews();
  state.session = getSession();

  const config = state.config;
  const payload = createPageEvent(
    state.anonymousId,
    state.userId,
    state.session!,
    null,
    name,
    category,
    properties,
    leanContextEnabled()
  );
  afterTcfAnswer(() => {
    void processAndSendEvent(payload, config, state.beforeSendCallbacks, state.afterSendCallbacks);
  });
}

/**
 * Track a custom event
 */
function track(event: string, properties?: Record<string, unknown>): void {
  if (!state.initialized || !state.config) {
    warn('CDP SDK not initialized. Call init() first.');
    return;
  }

  touchSession();
  state.session = getSession();

  const config = state.config;

  // Built and buffered SYNCHRONOUSLY (outside the TCF wait). REQUIRED for events
  // emitted during page unload — e.g. the engagement roll-up fired from a plugin's
  // onUnload: the core unload handler calls flushBatchOnUnload synchronously right
  // after plugin.onUnload, so the event must be buffered HERE; one buffered in a
  // later microtask misses the beacon flush and is lost. Until 20.10 this was true
  // only once a fingerprint had resolved, so a site with `fingerprint: 'off'` (Paga
  // Shop) built every track in a microtask; with no fingerprint anywhere (ЛО-13)
  // there is nothing left to wait for.
  const payload = createTrackEvent(
    state.anonymousId,
    state.userId,
    state.session!,
    null,
    event,
    properties,
    leanContextEnabled()
  );
  afterTcfAnswer(() => {
    void processAndSendEvent(payload, config, state.beforeSendCallbacks, state.afterSendCallbacks);
  });
}

/**
 * Identify a user
 */
function identify(userId: string, traits?: Record<string, unknown>): void {
  if (!state.initialized || !state.config) {
    warn('CDP SDK not initialized. Call init() first.');
    return;
  }

  // Р-ID6 (NEWS-LOGIN-IDENTITY-2026-10-26): an e-mail never becomes the userId. The userId sits in a cookie
  // for a year, readable by every script on the page; the plugins (CloudCart, WooCommerce) and hand-written
  // snippets pass the e-mail, so it is swapped here for the salted hash, and the e-mail rides in the traits.
  const email = normalizeEmail(userId);
  if (email) {
    const withEmail = { ...(traits || {}), email: (traits && traits.email) || email };
    identityUid(state.config.writeKey, email)
      .then((uid) => identifyAs(uid, withEmail))
      .catch(() => identifyAs(null, withEmail)); // no SubtleCrypto (plain http): no userId at all
    return;
  }
  identifyAs(userId, traits);
}

function identifyAs(userId: string | null, traits?: Record<string, unknown>): void {
  if (!state.initialized || !state.config) return;

  // Set user ID
  if (userId) {
    setUserId(userId, state.config.cookieExpiration, state.config.cookieDomain);
    state.userId = userId;
  }

  touchSession();
  state.session = getSession();

  const config = state.config;
  const payload = createIdentifyEvent(
    state.anonymousId,
    state.userId,
    state.session!,
    null,
    traits
  );
  afterTcfAnswer(() => {
    void processAndSendEvent(payload, config, state.beforeSendCallbacks, state.afterSendCallbacks);
  });
}

/**
 * An identify carrying `context.identity` for the sign-in module (core/auth-identity.ts): the login form's
 * candidate (no userId yet — the server holds the e-mail), its confirmation, a discard, or a verified page.
 */
function sendIdentity(opts: {
  userId: string | null;
  setUser: boolean;
  traits: Record<string, unknown>;
  identity: Record<string, unknown>;
}): void {
  if (!state.initialized || !state.config) return;
  if (opts.setUser && opts.userId) {
    setUserId(opts.userId, state.config.cookieExpiration, state.config.cookieDomain);
    state.userId = opts.userId;
  }
  touchSession();
  state.session = getSession();
  const config = state.config;
  const payload = createIdentifyEvent(
    state.anonymousId,
    opts.setUser ? state.userId : null,
    state.session!,
    null,
    opts.traits
  );
  (payload.context as unknown as Record<string, unknown>).identity = opts.identity;
  afterTcfAnswer(() => {
    void processAndSendEvent(payload, config, state.beforeSendCallbacks, state.afterSendCallbacks);
  });
}

/**
 * Associate user with a group
 */
function group(groupId: string, traits?: Record<string, unknown>): void {
  if (!state.initialized || !state.config) {
    warn('CDP SDK not initialized. Call init() first.');
    return;
  }

  touchSession();
  state.session = getSession();

  const config = state.config;
  const payload = createGroupEvent(
    state.anonymousId,
    state.userId,
    state.session!,
    null,
    groupId,
    traits
  );
  afterTcfAnswer(() => {
    void processAndSendEvent(payload, config, state.beforeSendCallbacks, state.afterSendCallbacks);
  });
}

// =============================================================================
// IDENTITY MANAGEMENT
// =============================================================================

/**
 * Get anonymous ID
 */
function getAnonymousIdFn(): string | null {
  return state.anonymousId;
}

/**
 * Get user ID
 */
function getUserIdFn(): string | null {
  return state.userId;
}

/**
 * Reset identity (logout)
 */
function reset(): void {
  if (!state.config) {
    return;
  }

  state.anonymousId = resetIdentity(
    state.config.cookieExpiration,
    state.config.cookieDomain
  );
  state.userId = null;
  state.session = getOrCreateSession(state.config.sessionTimeout);

  log('Identity reset, new anonymous ID:', state.anonymousId);
}

/**
 * Sign-out without a new browser identity: the userId is removed from state and storage, the anonymous id stays.
 * reset() mints a new anonymous id on the shared cookie domain — on a shop of a media that would split the reader too.
 */
function forgetUser(): void {
  if (!state.config) return;
  if (!state.userId && !getUserId()) return;
  try { clearUserId(state.config.cookieDomain); } catch { /* storage blocked */ }
  state.userId = null;
  log('User forgotten, anonymous ID kept:', state.anonymousId);
}

// =============================================================================
// CONSENT MANAGEMENT
// =============================================================================

/**
 * Update consent preferences
 */
function consent(preferences: ConsentPreferences): void {
  // Before init, just record the choice so init() resolves the right posture.
  if (!state.initialized || !state.config) {
    updateConsent(preferences, state.config?.cookieDomain);
    return;
  }

  // Apply via the shared upgrade path: persists the choice, exits lite mode and
  // syncs the grant to the server.
  const merged = applyConsentSignal(preferences, true);
  // The site's own call is a decision too: what waited for a TCF CMP goes now,
  // under this choice (LA-06).
  endTcfWait();

  // Emit a Consent Updated event once analytics is granted.
  if (merged.analytics) {
    track('Consent Updated', { preferences });
  }
}

/**
 * Get consent status
 */
function getConsentStatusFn(): ConsentPreferences {
  return getConsentStatus();
}

/**
 * Check if we have specific consent
 */
function hasConsent(type: ConsentType): boolean {
  return checkConsent(type);
}

// =============================================================================
// CONTEXT ACCESS
// =============================================================================

/**
 * Get current event context
 */
async function getContext(): Promise<EventContext> {
  if (!state.session) {
    state.session = getOrCreateSession(state.config?.sessionTimeout);
  }

  // No device block: nothing fingerprints (ЛО-13).
  return buildEventContext(state.session);
}

/**
 * Get current session
 */
function getSessionFn(): SessionData | null {
  return state.session;
}

/**
 * Get device fingerprint — always null since 20.10 (ЛО-13). Kept so a site that
 * calls it keeps working.
 */
async function getDeviceFingerprint(): Promise<string | null> {
  return null;
}

// =============================================================================
// PLUGIN SYSTEM
// =============================================================================

/**
 * Register a plugin
 */
function use(plugin: CDPPlugin): void {
  if (state.plugins.has(plugin.name)) {
    warn('Plugin already registered:', plugin.name);
    return;
  }

  state.plugins.set(plugin.name, plugin);

  // Install plugin with core API
  try {
    plugin.install(cdp);
  } catch (e) {
    warn('Plugin install error:', plugin.name, e);
  }

  // If already initialized, call onInit (once the document is parsed)
  if (state.initialized && state.config) {
    initPlugin(plugin, state.config);
  }

  log('Plugin registered:', plugin.name, plugin.version);
}

/**
 * Get a registered plugin
 */
function getPlugin<T extends CDPPlugin>(name: string): T | undefined {
  return state.plugins.get(name) as T | undefined;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Register a beforeSend callback
 */
function onBeforeSend(callback: BeforeSendCallback): void {
  state.beforeSendCallbacks.push(callback);
}

/**
 * Register an afterSend callback
 */
function onAfterSend(callback: AfterSendCallback): void {
  state.afterSendCallbacks.push(callback);
}

/**
 * Register an initialized callback
 */
function onInitialized(callback: InitializedCallback): void {
  if (state.initialized) {
    // Already initialized, call immediately
    callback();
  } else {
    state.initializedCallbacks.push(callback);
  }
}

// =============================================================================
// INTERNAL UTILITIES
// =============================================================================

/**
 * Send event via beacon (exposed for plugins)
 */
function sendBeaconFn(payload: EventPayload): boolean {
  if (!state.config) {
    return false;
  }
  return sendBeacon(state.config.apiHost, state.config.writeKey, payload);
}

// =============================================================================
// NAVIGATION TRACKING (SPA)
// =============================================================================

// Last URL we fired a navigation for — guards against duplicate soft-navs.
let lastTrackedUrl = '';

function setupNavigationTracking(): void {
  lastTrackedUrl = window.location.href;

  // Wrap BOTH pushState and replaceState — news SPAs / infinite-scroll commonly
  // update the URL via replaceState, which the old hook missed entirely.
  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    originalPushState.apply(history, args);
    handleNavigation();
  };

  const originalReplaceState = history.replaceState;
  history.replaceState = function (...args) {
    originalReplaceState.apply(history, args);
    handleNavigation();
  };

  // Back/forward + hash routing.
  window.addEventListener('popstate', handleNavigation);
  window.addEventListener('hashchange', handleNavigation);
}

function handleNavigation(): void {
  const url = window.location.href;
  // Dedupe: replaceState often fires with an unchanged URL (state-only updates).
  if (url === lastTrackedUrl) return;
  lastTrackedUrl = url;

  // A new page view earns one full context again (SiteProfile.events
  // .leanContext). Must happen BEFORE the plugins are told, because a plugin's
  // onPageChange can emit the first event of the new page synchronously.
  resetContextEpoch();

  // Notify plugins
  for (const plugin of state.plugins.values()) {
    if (plugin.onPageChange) {
      try {
        plugin.onPageChange(url);
      } catch (e) {
        warn('Plugin onPageChange error:', plugin.name, e);
      }
    }
  }

  // Auto-track page view if enabled
  if (state.config?.autoTrack) {
    page();
  }
}

// =============================================================================
// UNLOAD HANDLING
// =============================================================================

function setupUnloadHandler(): void {
  window.addEventListener('beforeunload', () => handleUnload('unload'));
  window.addEventListener('pagehide', () => handleUnload('unload'));
  // visibilitychange→hidden is the most reliable "leaving" signal on mobile,
  // where pagehide/beforeunload are often not fired when the tab is backgrounded
  // or the app is swiped away. Flush the batch here too (beacon survives it).
  //
  // But hidden ≠ gone: the tab usually comes back. Plugins get a distinct
  // onHidden for that (checkpoint, don't tear down); those without one keep
  // the historical onUnload fallback. Measured 2026-08-20: the news plugin
  // destroying its article on every tab-hide made 25% of dir.bg reading
  // sessions duplicates of the same visitor×article (8% before the beacon
  // fix made the hidden-path roll-ups actually arrive).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') handleUnload('hidden');
  });
}

/**
 * Whether the remote Site Profile turned on the lean context. Read per event
 * (not cached) so a dashboard save takes effect on the profile's next refresh
 * without a reload — and so turning it OFF is equally immediate, which is what
 * makes it a usable kill switch.
 */
function leanContextEnabled(): boolean {
  return state.siteProfile?.events?.leanContext === true;
}

function handleUnload(reason: 'hidden' | 'unload'): void {
  // Notify plugins
  for (const plugin of state.plugins.values()) {
    const hook = reason === 'hidden' ? (plugin.onHidden || plugin.onUnload) : plugin.onUnload;
    if (hook) {
      try {
        hook.call(plugin);
      } catch (e) {
        // Can't log during unload
      }
    }
  }

  // Flush buffered (batched) events via beacon before the page goes away.
  // Beacon failures fall back into the retry queue, which persistQueue() saves.
  if (state.config) {
    try {
      flushBatchOnUnload(state.config);
    } catch (e) {
      // Can't log during unload
    }
  }

  // Persist any remaining queued events
  persistQueue();
}

// =============================================================================
// PUBLIC API
// =============================================================================

const cdp: CDPCoreAPI = {
  // Initialization
  init,
  isInitialized,
  getConfig,
  getSiteProfile: getSiteProfileFn,
  whenConfigReady: whenConfigReadyFn,

  // Tracking
  page,
  track,
  identify,
  group,

  // Identity
  getAnonymousId: getAnonymousIdFn,
  getUserId: getUserIdFn,
  reset,
  forgetUser,

  // Consent
  consent,
  getConsentStatus: getConsentStatusFn,
  showConsentBanner: showConsentBannerFn,
  hasConsent,

  // Context
  getContext,
  getSession: getSessionFn,
  getDeviceFingerprint,

  // Plugins
  use,
  getPlugin,

  // Hooks
  onBeforeSend,
  onAfterSend,
  onInitialized,

  // Utilities
  log,
  sendBeacon: sendBeaconFn,
};

// =============================================================================
// EXPORTS
// =============================================================================

export { cdp };
export default cdp;

// Export types for plugins
export type {
  CDPCoreAPI,
  CDPCoreConfig,
  CDPPlugin,
  ConsentPreferences,
  ConsentType,
  EventContext,
  EventPayload,
  SessionData,
  DeviceFingerprint,
};
